import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { Router, RouterModule } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import PasswordValidation from '../../core/password-validators';
import { ApplicationDataService } from '../../core/services/application-data.service';
import { AlertDialogComponent } from '../../shared/alert-dialog/alert-dialog.component';
import { EncryptionDescriptionService } from '../../core/services/encryption-decryption.service';
import { NgIf } from '@angular/common';

@Component({
  selector: 'app-forget-password',
  standalone: true,
  imports: [
    MatCardModule,
    MatInputModule,
    MatFormFieldModule,
    TranslateModule,
    ReactiveFormsModule,
    MatButtonModule,
    RouterModule,
    NgIf,
  ],
  templateUrl: './forget-password.component.html',
  styleUrls: ['./forget-password.component.css'],
})
export class ForgetPasswordComponent implements OnInit {
  form: FormGroup;
  resendTimer: number = 0; // Timer countdown in seconds
  retryActive: boolean = false; // Control retry button state
  private timerInterval: any; // Interval for the timer

  constructor(
    private formBuilder: FormBuilder,
    private appDataService: ApplicationDataService,
    private dialog: MatDialog,
    private cryptService: EncryptionDescriptionService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group(
      {
        userName: ['', [Validators.required, Validators.maxLength(30)]],
        otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.maxLength(30),
            Validators.pattern(
              '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])[A-Za-z\\d$@$!%*?&]{8,}$'
            ),
          ],
        ],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: this.matchPasswordValidator }
    );
  }
  onOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, ''); // Remove non-numeric characters
    if (input.value.length > 6) {
      input.value = input.value.slice(0, 6); // Limit to 6 digits
    }
    this.form.controls['otp'].setValue(input.value); // Update the form control
  }
  
  matchPasswordValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;


    if (!password || !confirmPassword) {
      return null;
    }

    return password === confirmPassword ? null : { matchPassword: true };
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  
  
  sendCode(): void {
    if (this.f['userName'].invalid) {
      return;
    }
  
    this.appDataService.forgetPassword(this.f['userName'].value).subscribe({
      next: () => {
        this.openAlert('OTP has been sent.');
        this.f['userName'].disable(); // Disable the username field
        this.startTimer(); // Start the OTP timer
      },
      error: (err) => {
        const errorMessage = err?.error?.message || 'An error occurred while sending the OTP.';
        this.openAlert(errorMessage); // Use the backend error message
      },
    });
  }
  
  

  startTimer(): void {
    this.resendTimer = 120; 
    this.retryActive = false;
  
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  
    this.timerInterval = setInterval(() => {
      if (this.resendTimer > 0) {
        this.resendTimer--;
      } else {
        clearInterval(this.timerInterval);
        this.retryActive = true;
        this.f['userName'].enable();
      }
    }, 1000);
  }
  

  get formattedTimer(): string {
    const minutes = Math.floor(this.resendTimer / 60);
    const seconds = this.resendTimer % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }
  

  savePassword(): void {
    if (this.form.invalid) {
      return;
    }
  
    const payload = {
      userName: this.f['userName'].value, 
      password: this.cryptService.encryptWithPublicKey(this.f['password'].value), 
      otp: this.f['otp'].value, 
    };
  
    this.appDataService.saveNewPassword(payload).subscribe({
      next: () => {
        const dialogRef = this.openAlert('Password saved successfully.');
        dialogRef.afterClosed().subscribe(() => {
          this.router.navigate(['/login']);
        });
      },
      error: (error) => {
        this.openAlert(error?.error?.message);
      },
    });
  }


  openAlert(message: string): MatDialogRef<AlertDialogComponent> {
    return this.dialog.open(AlertDialogComponent, {
      data: {
        message,
        buttonText: {
          cancel: 'OK',
        },
      },
    });
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }
}
